import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { canAddProjects, canCreateProjectInEffort, canEditEffortProjects } from './project-permissions';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProjectsService } from '../services/projects.service';
import { Project } from '../models/project';
import { ProjectEditorComponent } from '../dialogs/project-editor/project-editor.component';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FilterEditorComponent } from '../components/filter-editor/filter-editor.component';
import { FilterChip } from '../models/filter-chip';
import { BehaviorSubject, combineLatest, debounceTime, distinct, firstValueFrom, map, skip, startWith, Subject, Subscription, take } from 'rxjs';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { ConfirmComponent } from '../dialogs/confirm/confirm.component';
import { FilterChipUtilityService } from '../services/filter-chip-utility.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../environments/environment';
import { UsersService } from '../services/user.service';
import { TableUIPreferences, UIPreference } from '../models/ui-preferences';
import { User } from '../models/user';
import { Effort } from '../models/effort';
import { EffortsService } from '../services/efforts.service';
import { GroupsService } from '../services/groups.service';
import { ProjectMembersService } from '../services/project-members.service';
import { BatchAddRemoveFromUsersComponent } from '../dialogs/batch-add-remove-from-users/batch-add-remove-from-users.component';

@Component({
  selector: 'app-project-management',
  templateUrl: './project-management.component.html',
  styleUrls: ['./project-management.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatExpansionModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatSelectModule,
    FormsModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    FilterEditorComponent,
    MatProgressSpinnerModule,
    RouterModule
  ],
})
export class ProjectManagementComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private effortsById = new Map<number, Effort>();
  displayedColumns: string[] = ['title', 'effortTitle', 'owner', 'customer', 'oAccount', 'mfk', 'members'];
  dataSource = new MatTableDataSource<Project>();
  filterControl = new FormControl('');
  selectedFilters: Subject<FilterChip[]> = new BehaviorSubject<FilterChip[]>([]);
  _loading = new BehaviorSubject<boolean>(false);
  isLoading = this._loading.pipe(debounceTime(environment.spinnerDelay));

  userService = inject(UsersService);
  deptService = inject(GroupsService);
  projMembershipService = inject(ProjectMembersService);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  subscription?: Subscription;
  filters?: FilterChip[];
  totalProjects: number = 0;
  private _dataChangedSubject = new Subject<FilterChip[]>();
  private dataChanged$ = this._dataChangedSubject.asObservable();
  private _refreshesSubject = new BehaviorSubject<boolean>(true);
  refreshes$ = this._refreshesSubject.asObservable();
  currentUser?: User;
  uiPreference?: UIPreference;
  pageSubscr?: Subscription;
  pagiSubscr?: Subscription;
  paramsSubscr?: Subscription;
  canAdd: boolean = false;
  effort?: Effort;
  user?: User;
  pageSize = 10;
  readonly chipHelpers = {
    "title": "Filters by Project's title",
    "key": "Filters by Project's unique key",
    "dept": "Filters by Project's containing department",
    "effort": "Filters by Project's parent Effort",
    "mfk": "Filters by Project's MFK, if it has one",
    "oaccount": "Filters by Project's OAccount, if it has one",
    "member.hawkid": "Filters by whether a member has particular Hawk Id",
    "archived": "Filters by whether a Project has been marked inactive",
  };
  readonly chipPossVals: { [key: string]: string[] | ((v: string) => Promise<string[] | string[][]>) } = {
    'member.hawkid': (value: string) =>
      firstValueFrom(this.userService.filterUsers([{ chip: 'hawkid', value }]).pipe(map((o => o.users.map(u => [u.hawkId, `${u.displayName} (${u.hawkId})`]))))),
    "mfk": (value: string) =>
      firstValueFrom(this.projectsService.filterProjects([{ chip: 'mfk', value }]).pipe(map((o => o.projects.map(u => [u.mfk, `${u.mfk.trim().length ? u.mfk : '[None]'} (${u.title})`]))))),
    "dept": (value: string) =>
      firstValueFrom(this.deptService.filterGroups([{ chip: null, value }]).pipe(map((o => o.groups.map(u => [u.key, `${u.name} (${u.key})`]))))),
    "effort": (value: string) =>
      firstValueFrom(this.effortService.filterEfforts([{ chip: null, value }]).pipe(map((o => o.efforts.map(u => [u.key, `${u.title} (${u.key})`]))))),
    "oaccount": (value: string) =>
      firstValueFrom(this.projectsService.filterProjects([{ chip: 'oaccount', value }]).pipe(map((o => o.projects.map(u => [u.oAccount, `${u.oAccount.trim().length ? u.oAccount : '[None]'} (${u.title})`]))))),
  }

  constructor(
    private projectsService: ProjectsService, private dialog: MatDialog, private route: ActivatedRoute, private router: Router, private cdr: ChangeDetectorRef, private filterUtility: FilterChipUtilityService, private effortService: EffortsService
  ) {
    this.userService.getCurrentUser().subscribe(user => {
      this.currentUser = user;
      this.uiPreference = user.userUIPreference?.data ? JSON.parse(user.userUIPreference.data) : new UIPreference();
      this.pageSize = this.uiPreference?.projects?.pageSize ?? 10;
      // console.log("UI Preferences", uiPreference, user.userUIPreference);
      this.canAdd = canAddProjects(user);
      if (this.canAdd) {
        // Project search does not include effort owners; effort search does.
        this.effortService.filterEfforts([], 10000).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(result => this.effortsById = new Map(result.efforts.map(effort => [effort.id, effort])));
      }
    });
  }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    this.pagiSubscr =combineLatest([
      this.sort.sortChange.pipe(startWith({ active: this.sort.active, direction: this.sort.direction })),
      this.paginator.page.pipe(startWith({ pageSize: this.paginator.pageSize, pageIndex: this.paginator.pageIndex })),
      this.dataChanged$.pipe(debounceTime(100)),
      this.refreshes$.pipe(debounceTime(100))
    ])
      .subscribe(([sort, page, filters]) => {
        console.log("paaaging", page.pageSize, page.pageIndex);
        this.subscription?.unsubscribe();
        this.subscription = this.projectsService.filterProjects(filters, page.pageSize, page.pageIndex, [[sort.active, sort.direction]], false).subscribe(resp => {
          console.log(resp);
          this._loading.next(false);
          this.filters = filters;
          this.dataSource.data = resp.projects;
          this.totalProjects = resp.total;
        });
      });
    this.paramsSubscr = this.route.queryParams.subscribe(this.queryParamsChanged.bind(this));
    this.pageSubscr = this.paginator.page
      .pipe(map(ev => ev.pageSize), distinct())
      .subscribe(size => {
        if (!this.uiPreference) return;

        this.uiPreference!.projects ??= new TableUIPreferences();
        this.uiPreference.projects.pageSize = size;
        this.userService.savePreference(this.uiPreference!, this.currentUser!.id).subscribe(uipref => {
          console.info("Saved pageSize preference.");
        });
      });
  }

  ngOnDestroy() {
    this.pageSubscr?.unsubscribe();
    this.subscription?.unsubscribe();
    this.paramsSubscr?.unsubscribe();
    this.pagiSubscr?.unsubscribe();
  }

  queryParamsChanged(p: Params) {
    const filters = [];
    if (p["filter.key"]) filters.push({ chip: 'key', value: p["filter.key"] });
    if (p["filter.title"]) filters.push({ chip: 'title', value: p["filter.title"] });
    if (p["filter.dept"]) filters.push({ chip: 'dept', value: p["filter.dept"] });
    if (p["filter.effort"]) filters.push({ chip: 'effort', value: p["filter.effort"] });
    if (p["filter.owner"]) filters.push({ chip: 'owner', value: p["filter.owner"] });
    if (p["filter.customer"]) filters.push({ chip: 'customer', value: p["filter.customer"] });
    if (p["filter.mfk"]) filters.push({ chip: 'mfk', value: p["filter.mfk"] });
    if (p["filter.oaccount"]) filters.push({ chip: 'oaccount', value: p["filter.oaccount"] });
    if (p["filter.keywords"]) filters.push({ chip: null, value: p["filter.keywords"] });
    if (p["filter.member.hawkid"]) filters.push({ chip: 'member.hawkid', value: p["filter.member.hawkid"] });
    if (p["filter.archived"]) filters.push({ chip: 'archived', value: '1', isFlag: true });
    this.paginator.firstPage();
    this.filters = filters;
    this.selectedFilters.next(filters);
    this._dataChangedSubject.next(filters);
  }

  openEditDialog(project: Project): void {
    if (!this.canModify(project)) return;
    project = { ...project, effort: this.effortsById.get(project.effortId) ?? project.effort };
    console.log(project);
    const dialogRef = this.dialog.open(ProjectEditorComponent, { data: { project }, width: '100%', maxWidth: '768px' });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.projectsService.updateProject(result).subscribe(() => this._dataChangedSubject.next(this.filters ?? []));
      }
    });
  }

  canAddToEffort(effort: Effort): boolean {
    return canCreateProjectInEffort(this.currentUser, effort);
  }

  openCreateDialog(targetEffort?: Effort): void {
    if (!this.canAdd) return;
    if (targetEffort && !this.canAddToEffort(targetEffort)) return;
    const effort = targetEffort
      ? { chip: 'effort', value: targetEffort.key }
      : this.filters?.find(f => f.chip == "effort");
    console.log(effort, this.filters);
    const dialogRef = this.dialog.open(ProjectEditorComponent, { data: { suggestedEffort: effort }, width: '50%' });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log("Saving created project");
        this.projectsService.createProject(result).subscribe(() => this._dataChangedSubject.next(this.filters ?? []));
      }
    });
  }

  canModify(project: Project) {
    return canEditEffortProjects(this.currentUser, this.effortsById.get(project.effortId) ?? project.effort);
  }

  effortSubscription?: Subscription;
  userSubscription?: Subscription;
  filterChange(filters: FilterChip[]): void {
    console.log("Filter changed", filters);
    this.filterUtility.updateUrlWithFilters(filters, '/projects');
    this._loading.next(true);
    this._dataChangedSubject.next(filters);

    const clear = (col: string) => {
      this.displayedColumns = this.displayedColumns.filter(dc => dc != col);
    }
    const add = (col: string) => {
      this.displayedColumns = this.displayedColumns.find(ac => ac == col) ? this.displayedColumns : [...this.displayedColumns, col]
    }

    const effortFilter = filters.find(f => f.chip == "effort");
    const userFilter = filters.find(f => f.chip == "member.hawkid");

    if (userFilter) {
      this.userSubscription?.unsubscribe();
      this.userSubscription = this.userService.filterUsers([{ chip: 'hawkid', value: userFilter.value }]).subscribe(res => {
        console.log(res);
        this.displayedColumns = this.displayedColumns.filter(dc => dc != 'tools');
        if (res.total == 1) {
          this.user = res.users[0];
          this.displayedColumns = [... this.displayedColumns, 'tools'];
        } else {
          this.user = undefined;
        }
      });
    } else {
      this.user = undefined;
    }

    // const pstCol = "effort-specific-tools";
    if (effortFilter) {
      this.effortSubscription?.unsubscribe();
      this.effortSubscription = this.effortService.filterEfforts([{ chip: 'key', value: effortFilter.value }]).subscribe(res => {
        console.log(res);
        if (res.total == 1) {
          this.effort = res.efforts[0];
          // add(pstCol);
        } else {
          this.effort = undefined;
          // clear(pstCol);
        }
      })
    } else {
      this.effort = undefined;
      // clear(pstCol);
    }
  }

  clearFilter(which: string) { this.selectedFilters.next(this.filters?.filter(f => f.chip !== which) ?? []); return false; }

  deleteMembership(project: Project, user: User) {
    console.info(this.deleteMembership.name);
    const data = { message: `Remove user '${user.displayName}' from project '${project.title}'?` };
    this.dialog.open(ConfirmComponent, { data }).afterClosed()
      .subscribe(yes => {
        if (yes && project.id) {
          this.projMembershipService.sourceUpdated$.pipe(skip(1), debounceTime(100), take(1)).subscribe(() => this._refreshesSubject.next(true));
          this.projMembershipService.deleteProjectMembership(project.id, user.id);
        }
      })
  }

    openBatchEditDialog(user: User): void {
      const dialogRef = this.dialog.open(BatchAddRemoveFromUsersComponent, { data: { user }, minWidth: '40em', maxWidth: '100vw' });
      dialogRef.afterClosed().subscribe(result => {
        console.log("Saving changes", result);
        this._refreshesSubject.next(true);
      });
    }
}
